import { ChangeDetectionStrategy, Component, computed, Signal, viewChild, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { debounceTime, distinctUntilChanged, from, Subject, switchMap } from 'rxjs';
import { debugRx } from '../../../common/utilities/debug-rxjs';
import { Address, Operator, PostalCode, SearchInput } from '../../../generated/generated-types';
import { PostalCodeService } from '../../../shared/services/postalcode.service';

@Component({
  selector: 'app-formly-postalCode-auto-complete',
  templateUrl: './formly-postalcode-auto-complete.component.html',
  styleUrl: './formly-postalcode-auto-complete.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PostalCodeService],
  standalone: true,
  imports: [
    AutoComplete,
    ReactiveFormsModule,
    FormlyModule,
  ]
})
export class FormlyPostalcodeAutoCompleteComponent extends FieldType {
  autoComplete = viewChild<AutoComplete>('autoComplete');
  debounceTime: number = 50;

  searchSub$ = new Subject<string | null>();

  readonly currentItemsSig: Signal<PostalCode[]>
  currentItemsComputed = computed(() => this.currentItemsSig());

  constructor(
    private postalCodeService: PostalCodeService
  ) {
    super();
    this.currentItemsSig = toSignal(this.searchSub$.pipe(
      takeUntilDestroyed(),
      distinctUntilChanged(),
      debounceTime(this.debounceTime),
      debugRx("PostalCode AutoComplete Subject created"),
      switchMap((term) => {
        if (term?.length > 2) {
          let si: SearchInput = {
            term: term
          };

          let countryIso = (this.model as Address).primaryPostalAddress?.countryIso ?? this.model.postalAddress?.countryIso;
          if (countryIso) {
            si.searchFilter = {
              field: "countryIso",
              operator: Operator.equal,
              value: countryIso
            }
          }

          return this.postalCodeService.getItems(si).mapSingle(x => x.getPostalCodes.items) ?? [];
        }
        return from([term]); // return empty object array if the term length is less than 2
      }),
      debugRx("PostalCode AutoComplete Subject created")
    ), { initialValue: [] });
  }

  async complete(e: AutoCompleteCompleteEvent) {
    const query = e.query;

    // Check if the query length is less than 3.
    if (query.length < 3) {
      this.autoComplete().loading = false;
      this.autoComplete().hide(true);
      this.searchSub$.next(null);
      return;
    }
    // Continue the search for valid terms
    this.searchSub$.next(query);
  }

  onSelect(e: AutoCompleteSelectEvent) {
    let val = e.value as PostalCode;

    this.formControl.setValue(val.postalCode);

    // Only patch the address-related sibling controls instead of cloning and patching the
    // entire parent FormGroup. When the parent is the root form (e.g. tenant with flat keys),
    // patching everything triggered valueChanges on unrelated controls (featuredAsset, etc.),
    // causing unwanted auto-saves that reset the form and discarded the city value (DEV-2710).
    const parentGroup = this.formControl.parent as FormGroup;
    const patchData: Record<string, any> = {};

    if (val.city != null) patchData.city = val.city;
    if (val.state != null) patchData.state = val.state;
    if (val.province != null) patchData.province = val.province;

    const countryControl = parentGroup.get('countryIso');
    if (countryControl && !countryControl.value) {
      patchData.countryIso = val.countryIso;
    }

    parentGroup.patchValue(patchData, { emitEvent: true });
  }
}
